/**
 * HostingLogsViewer.tsx — Phase 2 #8.
 *
 * Live `tail -F` of a customer's logs streamed over SSE. Renders the
 * scrollback in a terminal-style panel. Pause stops auto-scroll. Clear
 * wipes the buffer (doesn't affect the file). Source dropdown lets the
 * customer pick apex/subdomain/PHP.
 *
 * Uses native EventSource which sends the auth cookie automatically on
 * same-origin requests.
 */

import React, { useEffect, useRef, useState } from 'react';

interface LogSource { key: string; label: string; path: string; }

const MAX_BUFFER_LINES = 2000;

const HostingLogsViewer: React.FC<{ hostingAccountId: string }> = ({ hostingAccountId }) => {
  const apiBase = process.env.REACT_APP_API_URL || '';
  const [sources, setSources] = useState<LogSource[]>([]);
  const [source, setSource] = useState<string>('access');
  const [lines, setLines] = useState<string[]>([]);
  const [paused, setPaused] = useState(false);
  const [connected, setConnected] = useState(false);
  const [err, setErr] = useState('');
  const esRef = useRef<EventSource | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${apiBase}/api/hosting/accounts/${hostingAccountId}/logs`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(j => {
        if (j.success) {
          setSources(j.data.sources);
          if (j.data.sources.length > 0) setSource(j.data.sources[0].key);
        } else setErr(j.error || 'Failed to load sources');
      })
      .catch(e => setErr(e.message || 'Network error'));
    // eslint-disable-next-line
  }, [hostingAccountId]);

  // (Re)connect when source changes.
  useEffect(() => {
    if (!source) return;
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    setLines([]);
    setErr('');
    setConnected(false);

    // EventSource doesn't accept Authorization headers. We rely on the
    // session cookie. Need withCredentials so the cookie is sent on
    // cross-origin (in case the frontend is served from a different host).
    const url = `${apiBase}/api/hosting/accounts/${hostingAccountId}/logs/stream?source=${encodeURIComponent(source)}`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => setConnected(true);
    es.onmessage = (ev) => {
      setLines(prev => {
        const next = prev.concat(ev.data);
        return next.length > MAX_BUFFER_LINES ? next.slice(next.length - MAX_BUFFER_LINES) : next;
      });
    };
    es.addEventListener('error', () => {
      setErr('Stream error (server may have closed). Reload the panel to reconnect.');
      setConnected(false);
      es.close();
    });
    es.addEventListener('end', () => {
      setConnected(false);
      es.close();
    });
    es.addEventListener('timeout', () => {
      setErr('Stream auto-closed after 30 minutes. Reload to reconnect.');
      setConnected(false);
      es.close();
    });
    es.addEventListener('stderr', (ev: any) => {
      try {
        const j = JSON.parse(ev.data);
        if (j.text) {
          setLines(prev => prev.concat(`[stderr] ${j.text.trim()}`));
        }
      } catch { /* */ }
    });

    return () => { es.close(); esRef.current = null; };
  }, [source, hostingAccountId, apiBase]);

  // Auto-scroll to bottom unless paused.
  useEffect(() => {
    if (paused) return;
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [lines, paused]);

  return (
    <div className="mt-6 bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
        <div>
          <h2 className="font-semibold text-gray-900">Logs</h2>
          <p className="text-xs text-gray-500 mt-0.5">Live tail of nginx + PHP logs in your container.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={source}
            onChange={e => setSource(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            {sources.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <button
            onClick={() => setPaused(p => !p)}
            className={`text-xs px-2 py-1 rounded ${paused ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {paused ? 'Resume auto-scroll' : 'Pause auto-scroll'}
          </button>
          <button
            onClick={() => setLines([])}
            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded"
          >
            Clear
          </button>
          <span className={`text-xs ${connected ? 'text-green-700' : 'text-gray-500'}`}>
            {connected ? '● Live' : '○ Disconnected'}
          </span>
        </div>
      </div>

      {err && <div className="mb-2 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">{err}</div>}

      <div
        ref={scrollerRef}
        className="bg-black text-green-300 font-mono text-xs rounded p-3 overflow-y-auto"
        style={{ height: 360 }}
      >
        {lines.length === 0 ? (
          <div className="text-gray-500">{connected ? 'Waiting for output…' : 'Not connected.'}</div>
        ) : (
          lines.map((l, i) => (
            <div key={i} className="whitespace-pre-wrap break-all">{l}</div>
          ))
        )}
      </div>
    </div>
  );
};

export default HostingLogsViewer;
